import {
  ArrowRightOutlined,
  CalendarOutlined,
  ProjectOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Card, Col, Empty, Modal, Row, Select, Skeleton, Space, Table, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { BiHome } from "react-icons/bi";
import { Client } from "../../core/api-client";
import {
  API_GET_DASHBOARD_BENCH_PEOPLE,
  API_GET_DASHBOARD_BENCH_FY_STAFF,
  API_GET_DASHBOARD_LOW_PROJECT_ASSIGNMENT,
  API_GET_DASHBOARD_SUMMARY_COUNTS,  
} from "../../shared/routes/private.routes";
import "../../assets/styles/dashboard.less";

const { Text, Title } = Typography;

type SummaryCounts = {
  staff: {
    total: number;
    active: number;
    newJoiners: number;
    inactive: number;
    noticePeriod: number;
    draft: number;
  };
  project: {
    total: number;
    active: number;
    newProjects: number;
    inactive: number;
    draft: number;
  };
  alerts: {
    expiredProjectSow: ProjectSowAlert[];
    upcomingProjectSow: ProjectSowAlert[];
    expiredStaffContracts: StaffContractAlert[];
    upcomingStaffContracts: StaffContractAlert[];
  };
};

type ProjectSowAlert = {
  projectId: string;
  projectName: string;
  l2ProjectName?: string;
  sowNumber?: string;
  accountName?: string;
  endDate: string;
  daysRemaining: number;
};

type StaffContractAlert = {
  staffId: string;
  staffName: string;
  employeeId: string;
  clientName?: string;
  contractEndDate: string;
  daysRemaining: number;
};

type BenchPerson = {
  staffId: string;
  staffName: string;
  employeeId: string;
  designation?: string | null;
  projectAllocationPercent: number;
  lopPercent: number;
  benchPercent: number;
};
type BenchFYPerson = {
  staffId: string;
  staffName: string;
  employeeId: string;
  designation?: string | null;
  projectAllocationPercent: number;
  lopPercent: number;
  benchPercent: number;
};

type BenchMonthSummary = {
  month: string;
  label: string;
  total: number;
  people: BenchPerson[];
};
type BenchMonthFYSummary = {
  month: string;
  label: string;
  total: number;
  people: BenchFYPerson[];
};

type BenchMonthKey = "previousMonth" | "currentMonth" | "nextMonth";

type LowAssignmentPerson = {
  staffId: string;
  staffName: string;
  employeeId: string;
  designation?: string | null;
  projectAllocationPercent: number;
};

type LowAssignmentSummary = {
  month: string;
  label: string;
  total: number;
  people: LowAssignmentPerson[];
};

type DetailModalConfig =
  | {
      title: string;
      description: string;
      type: "expired" | "upcoming";
      tableType: "project" | "staff";
      data: ProjectSowAlert[] | StaffContractAlert[];
    }
  | {
      title: string;
      description: string;
      type?: "expired" | "upcoming";
      tableType: "bench";
      data: BenchPerson[];
    }
  | {
      title: string;
      description: string;
      type?: "expired" | "upcoming";
      tableType: "benchFY";
      data: BenchFYPerson[];
    }
  | {
      title: string;
      description: string;
      tableType: "lowAssignment";
      data: LowAssignmentPerson[];
    }
  | null;

const emptyCounts: SummaryCounts = {
  staff: {
    total: 0,
    active: 0,
    newJoiners: 0,
    inactive: 0,
    noticePeriod: 0,
    draft: 0,
  },
  project: {
    total: 0,
    newProjects: 0,
    draft: 0,
    active: 0,
    inactive: 0,
  },
  alerts: {
    expiredProjectSow: [],
    upcomingProjectSow: [],
    expiredStaffContracts: [],
    upcomingStaffContracts: [],
  },
};

const countItems = {
  staff: [
    { label: "New Joiners (Last 30 days)", key: "newJoiners", color: "processing" },
    { label: "Draft", key: "draft", color: "processing" },
    { label: "Inactive (Last 30 days)", key: "inactive", color: "default" },
  ],
  project: [
    { label: "New Projects (Last 30 days)", key: "newProjects", color: "processing" },
    { label: "Active", key: "active", color: "success" },
    { label: "Draft", key: "draft", color: "processing" },
    { label: "Inactive (Last 30 days)", key: "inactive", color: "default" },
  ],
};

const staffFeaturedItems = [
  { label: "Active", key: "active", color: "success" },
  { label: "Notice Period", key: "noticePeriod", color: "warning" },
];

const DashboardPage = () => {
  const [counts, setCounts] = useState<SummaryCounts>(emptyCounts);
  const [loading, setLoading] = useState(false);
  const [benchLoading, setBenchLoading] = useState(false);
  const [benchfyLoading, setBenchfyLoading] = useState(false);

  const [lowAssignmentLoading, setLowAssignmentLoading] = useState(false);
  const [benchMonths, setBenchMonths] = useState({
    previousMonth: getEmptyBenchMonth(getPreviousMonthValue()),
    currentMonth: getEmptyBenchMonth(getCurrentMonthValue()),
    nextMonth: getEmptyBenchMonth(getNextMonthValue()),
  });
  const [benchfyMonths, setBenchfyMonths] = useState({
    previousMonth: getEmptyBenchMonth(getPreviousMonthValue()),
    currentMonth: getEmptyBenchMonth(getCurrentMonthValue()),
    nextMonth: getEmptyBenchMonth(getNextMonthValue()),
  });
  const [lowAssignment, setLowAssignment] = useState<LowAssignmentSummary>(
    getEmptyLowAssignment(getCurrentMonthValue())
  );
  const [detailModal, setDetailModal] = useState<DetailModalConfig>(null);
  const [selectedBenchMonth, setSelectedBenchMonth] =
    useState<BenchMonthKey>("currentMonth");
    const [selectedBenchFYMonth, setSelectedBenchFYMonth] =
    useState<BenchMonthKey>("currentMonth");


  const loadCounts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Client.get(API_GET_DASHBOARD_SUMMARY_COUNTS);
      setCounts(normalizeCounts(response?.data?.data));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const benchPeople = benchMonths[selectedBenchMonth];
  const benchFYPeople = benchfyMonths[selectedBenchFYMonth];

  const loadBenchPeople = useCallback(async () => {
    const monthEntries: { key: BenchMonthKey; month: string }[] = [
      { key: "previousMonth", month: getPreviousMonthValue() },
      { key: "currentMonth", month: getCurrentMonthValue() },
      { key: "nextMonth", month: getNextMonthValue() },
    ];

    setBenchLoading(true);
    try {
      const responses = await Promise.all(
        monthEntries.map(({ month }) =>
          Client.get(API_GET_DASHBOARD_BENCH_PEOPLE, {
            params: { month },
          })
        )
      );

      setBenchMonths(
        monthEntries.reduce(
          (acc, entry, index) => ({
            ...acc,
            [entry.key]:
              responses[index]?.data?.data || getEmptyBenchMonth(entry.month),
          }),
          {} as Record<BenchMonthKey, BenchMonthSummary>
        )
      );
    } finally {
      setBenchLoading(false);
    }
  }, []);

  const loadBenchfyPeople = useCallback(async () => {
    const monthEntries: { key: BenchMonthKey; month: string }[] = [
      { key: "previousMonth", month: getPreviousMonthValue() },
      { key: "currentMonth", month: getCurrentMonthValue() },
      { key: "nextMonth", month: getNextMonthValue() },
    ];

    setBenchfyLoading(true);
    try {
      const responses = await Promise.all(
        monthEntries.map(({ month }) =>
          Client.get(API_GET_DASHBOARD_BENCH_FY_STAFF, {
            params: { month },
          })
        )
      );

      setBenchfyMonths(
        monthEntries.reduce(
          (acc, entry, index) => ({
            ...acc,
            [entry.key]:
              responses[index]?.data?.data || getEmptyBenchMonth(entry.month),
          }),
          {} as Record<BenchMonthKey, BenchMonthFYSummary>
        )
      );
    } finally {
      setBenchfyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBenchPeople();
    loadBenchfyPeople();
  }, [loadBenchPeople, loadBenchfyPeople]);

  const loadLowAssignment = useCallback(async () => {
    const month = getCurrentMonthValue();
    setLowAssignmentLoading(true);
    try {
      const response = await Client.get(
        API_GET_DASHBOARD_LOW_PROJECT_ASSIGNMENT,
        {
          params: { month },
        }
      );
      setLowAssignment(response?.data?.data || getEmptyLowAssignment(month));
    } finally {
      setLowAssignmentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLowAssignment();
  }, [loadLowAssignment]);

  return (
    <div className="app-page dashboard-page">
      <div className="app-page-header dashboard-page-header">
        <div>
          <div className="app-page-title-wrap">
            <span className="app-page-icon">
              <BiHome />
            </span>
            <Title level={1} className="app-page-title">
              Dashboard
            </Title>
          </div>
          <p className="app-page-subtitle">Staff and project status overview</p>
        </div>
        <button
          className="dashboard-refresh-button"
          type="button"
          onClick={loadCounts}
          aria-label="Refresh dashboard counts"
        >
          <ReloadOutlined />
          <span>Refresh</span>
        </button>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <SummaryCard
            title="Total Staff"
            icon={<TeamOutlined />}
            total={counts.staff.total}
            featuredItems={staffFeaturedItems.map((item) => ({
              ...item,
              value: counts.staff[item.key as keyof SummaryCounts["staff"]],
            }))}
            items={countItems.staff.map((item) => ({
              ...item,
              value: counts.staff[item.key as keyof SummaryCounts["staff"]],
            }))}
            loading={loading}
          />
        </Col>
        <Col xs={24} lg={12}>
          <SummaryCard
            title="Total Projects"
            icon={<ProjectOutlined />}
            total={counts.project.total}
            items={countItems.project.map((item) => ({
              ...item,
              value: counts.project[item.key as keyof SummaryCounts["project"]],
            }))}
            loading={loading}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="dashboard-alert-grid">
        <Col xs={24} xl={12}>
          <ProjectSowAlertTable
            title="1. Expired Project / SOW"
            description="Expired in the last 90 days"
            data={counts.alerts.expiredProjectSow}
            type="expired"
            loading={loading}
            onViewAll={() =>
              setDetailModal({
                title: "1. Expired Project / SOW",
                description: "Expired in the last 90 days",
                type: "expired",
                tableType: "project",
                data: counts.alerts.expiredProjectSow,
              })
            }
          />
        </Col>
        <Col xs={24} xl={12}>
          <ProjectSowAlertTable
            title="2. Upcoming Project / SOW Expiry"
            description="Expiring in the next 30 days"
            data={counts.alerts.upcomingProjectSow}
            type="upcoming"
            loading={loading}
            onViewAll={() =>
              setDetailModal({
                title: "2. Upcoming Project / SOW Expiry",
                description: "Expiring in the next 30 days",
                type: "upcoming",
                tableType: "project",
                data: counts.alerts.upcomingProjectSow,
              })
            }
          />
        </Col>
        <Col xs={24} xl={12}>
          <StaffContractAlertTable
            title="3. Expired Staff Contract"
            description="Expired in the last 90 days"
            data={counts.alerts.expiredStaffContracts}
            type="expired"
            loading={loading}
            onViewAll={() =>
              setDetailModal({
                title: "3. Expired Staff Contract",
                description: "Expired in the last 90 days",
                type: "expired",
                tableType: "staff",
                data: counts.alerts.expiredStaffContracts,
              })
            }
          />
        </Col>
        <Col xs={24} xl={12}>
          <StaffContractAlertTable
            title="4. Upcoming Staff Contract Expiry"
            description="Expiring in the next 30 days"
            data={counts.alerts.upcomingStaffContracts}
            type="upcoming"
            loading={loading}
            onViewAll={() =>
              setDetailModal({
                title: "4. Upcoming Staff Contract Expiry",
                description: "Expiring in the next 30 days",
                type: "upcoming",
                tableType: "staff",
                data: counts.alerts.upcomingStaffContracts,
              })
            }
          />
        </Col>
      </Row>

      <DashboardDetailModal
        config={detailModal}
        onClose={() => setDetailModal(null)}
      />

      <Row gutter={[16, 16]} className="dashboard-next-section-grid">
        <Col xs={24} xl={12}>
          <BenchPeopleSection
            month={benchPeople}
            months={benchMonths}
            selectedMonth={selectedBenchMonth}
            onSelectedMonthChange={setSelectedBenchMonth}
            loading={benchLoading}
            onViewAll={(month) =>
              setDetailModal({
                title: `5. Unallocated Staff - ${month.label}`,
                description: "Staff less than 100% allocation",
                tableType: "bench",
                data: month.people,
              })
            }
          />
        </Col>
        <Col xs={24} xl={12}>
          <BenchFyStaffSection
            month={benchFYPeople}
            months={benchfyMonths}
            selectedMonth={selectedBenchFYMonth}
            onSelectedMonthChange={setSelectedBenchFYMonth}
            loading={benchfyLoading}
            onViewAll={(month) =>
              setDetailModal({
                title: `6. Bench Staff List - ${month.label}`,
                description: "Staff who are tagged under Bench",
                tableType: "benchFY",
                data: month.people,
              })
            }
          />
        </Col>
        
        
      </Row>
    
    </div>
  );
};

type SummaryCardProps = {
  title: string;
  icon: React.ReactNode;
  total: number;
  loading: boolean;
  featuredItems?: {
    label: string;
    value: number;
    color: string;
  }[];
  items: {
    label: string;
    value: number;
    color: string;
  }[];
};

function SummaryCard({
  title,
  icon,
  total,
  featuredItems = [],
  items,
  loading,
}: SummaryCardProps) {
  const statusItems = [...featuredItems, ...items];

  return (
    <Card className="dashboard-summary-card" bordered={false}>
      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <>
          <div className="dashboard-summary-top">
            <div className="dashboard-summary-main">
              <span className="dashboard-summary-icon">{icon}</span>
              <Text className="dashboard-summary-title">{title}</Text>
            </div>
            <div className="dashboard-summary-total">{total}</div>
          </div>

          <div className="dashboard-status-grid">
            {statusItems.map((item) => (
              <div className="dashboard-status-item" key={item.label}>
                <Space size={8}>
                  <Tag color={item.color}>{item.label}</Tag>
                </Space>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

function LowAssignmentSection({
  data,
  loading,
  onViewAll,
}: {
  data: LowAssignmentSummary;
  loading: boolean;
  onViewAll: () => void;
}) {
  return (
    <Card
      className="dashboard-low-assignment-card"
      title={
        <div className="dashboard-bench-title">
          <span>5. People With No Project / Low Project Assignment</span>
          <span>Current month allocation less than or equal to 99%</span>
        </div>
      }
      bordered={false}
    >
      <div className="dashboard-low-assignment-count">
        <Tag color="processing">{data.total}</Tag>
      </div>
      <div className="dashboard-low-assignment-table-wrap">
        <Table<LowAssignmentPerson>
          size="small"
          loading={loading}
          rowKey="staffId"
          dataSource={data.people.slice(0, 5)}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={false}
          columns={getLowAssignmentColumns()}
        />
        <ViewAllFooter count={data.people.length} onViewAll={onViewAll} />
      </div>
    </Card>
  );
}

function normalizeCounts(data: Partial<SummaryCounts> | undefined): SummaryCounts {
  return {
    staff: {
      ...emptyCounts.staff,
      ...(data?.staff || {}),
    },
    project: {
      ...emptyCounts.project,
      ...(data?.project || {}),
    },
    alerts: {
      ...emptyCounts.alerts,
      ...(data?.alerts || {}),
    },
  };
}

function BenchPeopleSection({
  month,
  months,
  selectedMonth,
  onSelectedMonthChange,
  loading,
  onViewAll,
}: {
  month: BenchMonthSummary;
  months: {
    previousMonth: BenchMonthSummary;
    currentMonth: BenchMonthSummary;
    nextMonth: BenchMonthSummary;
  };
  selectedMonth: BenchMonthKey;
  onSelectedMonthChange: (month: BenchMonthKey) => void;
  loading: boolean;
  onViewAll: (month: BenchMonthSummary) => void;
}) {
  const monthOptions = [
    {
      label: `Previous Month (${months.previousMonth.label}) - ${months.previousMonth.total}`,
      value: "previousMonth",
    },
    {
      label: `Current Month (${months.currentMonth.label}) - ${months.currentMonth.total}`,
      value: "currentMonth",
    },
    {
      label: `Next Month (${months.nextMonth.label}) - ${months.nextMonth.total}`,
      value: "nextMonth",
    },
  ];

  return (
    <Card
      className="dashboard-bench-card"
      title={
        <div className="dashboard-bench-header">
          <div className="dashboard-bench-title">
            <span>5. Unallocated Staff</span>
            <span>Staff less than 100% allocation</span>
          </div>
          <Select<BenchMonthKey>
            className="dashboard-bench-month-select"
            value={selectedMonth}
            options={monthOptions}
            onChange={onSelectedMonthChange}
          />
        </div>
      }
      bordered={false}
    >
      <div className="dashboard-bench-table-wrap">
        <Table<BenchPerson>
          size="small"
          loading={loading}
          rowKey="staffId"
          dataSource={month.people.slice(0, 5)}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={false}
          columns={getBenchColumns()}
        />
        <ViewAllFooter
          count={month.people.length}
          onViewAll={() => onViewAll(month)}
        />
      </div>
    </Card>
  );
}

  function BenchFyStaffSection({
  month,
  months,
  selectedMonth,
  onSelectedMonthChange,
  loading,
  onViewAll,
}: {
  month: BenchMonthFYSummary;
  months: {
    previousMonth: BenchMonthFYSummary;
    currentMonth: BenchMonthFYSummary;
    nextMonth: BenchMonthFYSummary;
  };
  selectedMonth: BenchMonthKey;
  onSelectedMonthChange: (month: BenchMonthKey) => void;
  loading: boolean;
  onViewAll: (month: BenchMonthFYSummary) => void;
}) {
  const monthOptions = [
    {
      label: `Previous Month (${months.previousMonth.label}) - ${months.previousMonth.total}`,
      value: "previousMonth",
    },
    {
      label: `Current Month (${months.currentMonth.label}) - ${months.currentMonth.total}`,
      value: "currentMonth",
    },
    {
      label: `Next Month (${months.nextMonth.label}) - ${months.nextMonth.total}`,
      value: "nextMonth",
    },
  ];

  return (
    <Card
      className="dashboard-bench-card"
      title={
        <div className="dashboard-bench-header">
          <div className="dashboard-bench-title">
            <span>6. Bench Staff List</span>
            <span>Staff who are tagged under Bench</span>
          </div>

          <Select<BenchMonthKey>
            className="dashboard-bench-month-select"
            value={selectedMonth}
            options={monthOptions}
            onChange={onSelectedMonthChange}
          />
        </div>
      }
      bordered={false}
    >
      <div className="dashboard-bench-table-wrap">
        <Table<BenchFYPerson>
          size="small"
          loading={loading}
          rowKey="staffId"
          dataSource={month.people.slice(0, 5)}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />,
          }}
          pagination={false}
          columns={getBenchFYColumns()}
        />

        <ViewAllFooter
          count={month.people.length}
          onViewAll={() => onViewAll(month)}
        />
      </div>
    </Card>
  );
}

type AlertTableProps<T> = {
  title: string;
  description: string;
  data: T[];
  type: "expired" | "upcoming";
  loading: boolean;
  onViewAll: () => void;
};

function ProjectSowAlertTable({
  title,
  description,
  data,
  type,
  loading,
  onViewAll,
}: AlertTableProps<ProjectSowAlert>) {
  return (
    <Card
      className="dashboard-alert-card"
      title={
        <AlertTitle
          title={title}
          description={description}
          count={data.length}
          type={type}
        />
      }
      bordered={false}
    >
      <Table<ProjectSowAlert>
        size="small"
        loading={loading}
        rowKey="projectId"
        dataSource={data.slice(0, 5)}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        pagination={false}
        columns={getProjectSowColumns(type)}
      />
      <ViewAllFooter count={data.length} onViewAll={onViewAll} />
    </Card>
  );
}

function StaffContractAlertTable({
  title,
  description,
  data,
  type,
  loading,
  onViewAll,
}: AlertTableProps<StaffContractAlert>) {
  return (
    <Card
      className="dashboard-alert-card"
      title={
        <AlertTitle
          title={title}
          description={description}
          count={data.length}
          type={type}
        />
      }
      bordered={false}
    >
      <Table<StaffContractAlert>
        size="small"
        loading={loading}
        rowKey="staffId"
        dataSource={data.slice(0, 5)}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        pagination={false}
        columns={getStaffContractColumns(type)}
      />
      <ViewAllFooter count={data.length} onViewAll={onViewAll} />
    </Card>
  );
}

function ViewAllFooter({
  count,
  onViewAll,
}: {
  count: number;
  onViewAll: () => void;
}) {
  if (!count) {
    return null;
  }

  return (
    <button className="dashboard-view-all-button" type="button" onClick={onViewAll}>
      <span>View all</span>
      <ArrowRightOutlined />
    </button>
  );
}

function DashboardDetailModal({
  config,
  onClose,
}: {
  config: DetailModalConfig;
  onClose: () => void;
}) {
  if (!config) {
    return null;
  }

  const isProjectTable = config.tableType === "project";
  const isBenchTable = config.tableType === "bench";
  const isBenchFYTable = config.tableType === "benchFY";
  const isLowAssignmentTable = config.tableType === "lowAssignment";

  return (
    <Modal
      visible
      centered
      width="min(1080px, 92vw)"
      className="dashboard-detail-modal"
      footer={null}
      onCancel={onClose}
      title={
        <div className="dashboard-detail-title">
          <span>{config.title}</span>
          <span>{config.description}</span>
        </div>
      }
    >
      <div className="dashboard-detail-table-wrap">
      {isLowAssignmentTable ? (
        <Table<LowAssignmentPerson>
          size="small"
          rowKey="staffId"
          dataSource={config.data as LowAssignmentPerson[]}
          columns={getLowAssignmentColumns()}
          pagination={false}
          scroll={{ y: 480, x: 760 }}
        />
      ) : isBenchTable ? (
        <Table<BenchPerson>
          size="small"
          rowKey="staffId"
          dataSource={config.data as BenchPerson[]}
          columns={getBenchColumns()}
          pagination={false}
          scroll={{ y: 480, x: 760 }}
        />
      ): isBenchFYTable ? (
        <Table<BenchFYPerson>
          size="small"
          rowKey="staffId"
          dataSource={config.data as BenchFYPerson[]}
          columns={getBenchFYColumns()}
          pagination={false}
          scroll={{ y: 480, x: 760 }}
        />
      ): isProjectTable ? (
        <Table<ProjectSowAlert>
          size="small"
          rowKey="projectId"
          dataSource={config.data as ProjectSowAlert[]}
          columns={getProjectSowColumns(config.type || "expired")}
          pagination={false}
          scroll={{ y: 480, x: 760 }}
        />
      ) : (
        <Table<StaffContractAlert>
          size="small"
          rowKey="staffId"
          dataSource={config.data as StaffContractAlert[]}
          columns={getStaffContractColumns(config.type || "expired")}
          pagination={false}
          scroll={{ y: 480, x: 760 }}
        />
      )}
      </div>
    </Modal>
  );
}

function getBenchColumns() {
  return [
    {
      title: "Staff",
      dataIndex: "staffName",
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      ellipsis: true,
      render: (value: string | null) => value || "-",
    },
    {
      title: "Unallocated",
      dataIndex: "benchPercent",
      width: 120,
      align: "right" as const,
      render: (value: number) => (
        <span style={{ color: "#dc2626", display: "block", fontWeight: 600 }}>
          {value || 0}%
        </span>
      ),
    },
  ];
}

function getBenchFYColumns() {
  return [
    {
      title: "Staff",
      dataIndex: "staffName",
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      ellipsis: true,
      render: (value: string | null) => value || "-",
    },
    {
      title: "Bench %",
      dataIndex: "benchPercent",
      width: 120,
      align: "right" as const,
      render: (value: number) => (
        <span style={{ color: "#dc2626", display: "block", fontWeight: 600 }}>
          {value || 0}%
        </span>
      ),
    },
  ];
}
function getLowAssignmentColumns() {
  return [
    {
      title: "Staff",
      dataIndex: "staffName",
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      ellipsis: true,
      render: (value: string | null) => value || "-",
    },
    {
      title: "Current Month Allocation",
      dataIndex: "projectAllocationPercent",
      width: 210,
      render: (value: number) => <AllocationBar value={value || 0} />,
    },
  ];
}

function AllocationBar({ value }: { value: number }) {
  return (
    <div className="dashboard-allocation-bar">
      <div className="dashboard-allocation-track">
        <span style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} />
      </div>
      <strong>{value}%</strong>
    </div>
  );
}

function getProjectSowColumns(type: "expired" | "upcoming") {
  return [
    {
      title: "Project / SOW",
      dataIndex: "projectName",
      ellipsis: true,
      render: (_: string, row: ProjectSowAlert) => (
        <div>
          <strong>{row.l2ProjectName || row.projectName || "-"}</strong>
          <div className="dashboard-muted-text">SOW: {row.sowNumber || "-"}</div>
        </div>
      ),
    },
    {
      title: "Client",
      dataIndex: "accountName",
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      width: 120,
      render: formatDate,
    },
    {
      title: type === "expired" ? "Overdue" : "Days Left",
      dataIndex: "daysRemaining",
      width: 105,
      render: (value: number) => (
        <Tag
          color={type === "expired" ? "error" : "warning"}
          style={
            type === "expired"
              ? { color: "#dc2626", fontWeight: 600 }
              : undefined
          }
        >
          {type === "expired" ? Math.abs(value) : value} days
        </Tag>
      ),
    },
  ];
}

function getStaffContractColumns(type: "expired" | "upcoming") {
  return [
    {
      title: "Staff",
      dataIndex: "staffName",
      ellipsis: true,
      render: (_: string, row: StaffContractAlert) => (
        <div>
          <strong>{row.staffName || "-"}</strong>
          <div className="dashboard-muted-text">ID: {row.employeeId || "-"}</div>
        </div>
      ),
    },
    {
      title: "Client",
      dataIndex: "clientName",
      ellipsis: true,
      render: (value: string) => value || "-",
    },
    {
      title: "Contract End",
      dataIndex: "contractEndDate",
      width: 125,
      render: formatDate,
    },
    {
      title: type === "expired" ? "Overdue" : "Days Left",
      dataIndex: "daysRemaining",
      width: 105,
      render: (value: number) => (
        <Tag
          color={type === "expired" ? "error" : "warning"}
          style={
            type === "expired"
              ? { color: "#dc2626", fontWeight: 600 }
              : undefined
          }
        >
          {type === "expired" ? Math.abs(value) : value} days
        </Tag>
      ),
    },
  ];
}

function AlertTitle({
  title,
  description,
  count,
  type,
}: {
  title: string;
  description: string;
  count: number;
  type: "expired" | "upcoming";
}) {
  return (
    <div className="dashboard-alert-title">
      <div className="dashboard-alert-title-main">
        <Space size={8}>
          <CalendarOutlined />
          <span>{title}</span>
        </Space>
        <span className="dashboard-alert-window">{description}</span>
      </div>
      <Tag color={type === "expired" ? "error" : "warning"}>{count}</Tag>
    </div>
  );
}

function formatDate(value: string) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getCurrentMonthValue() {
  const now = new Date();
  return formatMonthValue(now);
}

function getPreviousMonthValue() {
  const previousMonth = new Date();
  previousMonth.setMonth(previousMonth.getMonth() - 1);
  return formatMonthValue(previousMonth);
}

function getNextMonthValue() {
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  return formatMonthValue(nextMonth);
}

function formatMonthValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function getMonthLabel(month: string) {
  const [year, monthValue] = month.split("-");
  const date = new Date(Number(year), Number(monthValue) - 1, 1);
  if (Number.isNaN(date.getTime())) {
    return month;
  }

  return date.toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });
}

function getEmptyBenchMonth(month: string): BenchMonthSummary {
  return {
    month,
    label: getMonthLabel(month),
    total: 0,
    people: [],
  };
}

function getEmptyLowAssignment(month: string): LowAssignmentSummary {
  return {
    month,
    label: getMonthLabel(month),
    total: 0,
    people: [],
  };
}

export default DashboardPage;
